import React, { useState, useMemo, useRef, useEffect } from "react";
import styled from "styled-components";
import { Coach as CoachModel } from "../model/Coach";
import { Division as DivisionModel, Week as WeekModel, PlayoffStructure } from "../model/Season";
import { createComponentLogger } from "../utils/logger";
import {
    TeamStats,
    isRegularSeasonWeekName,
    isWeekComplete,
    getStandingsThroughWeek,
    compareByTieBreakerLeagueWide,
    compareByTieBreakerWithinDivision,
    compareByTieBreakerPlayoffPool
} from "../utils/seasonStandingsCore";
import {
    TableContainer,
    TableHeader,
    TableHeaderCell as BaseTableHeaderCell,
    TableRow,
    TableCell as BaseTableCell,
    ChangeIndicator as BaseChangeIndicator
} from "./shared/TableComponents";
import { InfoIcon } from "./shared/InfoIcon";
import { StandingsBumpChart, BumpSeries } from "./StandingsBumpChart";
import { getClinchStatuses, ClinchReason } from "../utils/playoffClinch";

const BUMP_CHART_COLORS = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
];

const StandingsSection = styled.div`
    margin-top: 0.5em;
`;

// Custom grid with narrow rank column and compact rows
// When $showDivisionColumn, insert Division column after Rank (e.g. for league-wide / by-column view).
// On mobile, Rank and Coach columns are sticky. Coach sticks at 40px (next to Rank) so it scrolls until then.
const StandingsTableGrid = styled.div<{ $showExtended?: boolean; $showDivisionColumn?: boolean }>`
    --sticky-coach-left: 40px;
    display: grid;
    min-width: 1000px;
    grid-template-columns: ${props => {
        const base = props.$showExtended
            ? '40px 180px repeat(21, 1fr)'
            : '40px 180px repeat(13, 1fr)';
        if (props.$showDivisionColumn) {
            return props.$showExtended
                ? '40px 100px 180px repeat(21, 1fr)'
                : '40px 100px 180px repeat(13, 1fr)';
        }
        return base;
    }};
`;

// Division section header row: sticky cell (same pattern as Rank column) + spacer so the row aligns with the grid.
const StandingsDivisionHeaderCell = styled.div`
    background-color: #e9ecef;
    padding: 0.2em 0.4em;
    font-weight: 600;
    color: #495057;
    font-size: 0.8em;
    border-bottom: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
    border-left: 1px solid #dee2e6;
    grid-column: 1 / 3;
    font-family: inherit;
    display: flex;
    align-items: center;
    @media (max-width: 768px) {
        position: sticky;
        left: 0;
        z-index: 0;
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.06);
    }
`;

const StandingsDivisionHeaderSpacer = styled.div`
    grid-column: 3 / -1;
    background-color: #e9ecef;
    border-bottom: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
`;

/** Full-width message row (e.g. when playoff picture has no structure configured). */
const StandingsMessageRow = styled.div`
    grid-column: 1 / -1;
    padding: 1em;
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    color: #495057;
    font-size: 0.95em;
`;

const StandingsTableRow = styled(TableRow)<{ $isEven: boolean }>`
    & > * {
        background-color: ${props => props.$isEven ? '#f8f9fa' : 'white'} !important;
    }
`;

// Sort direction indicator shown in header when "By stat" is active (↑ or ↓)
const SortIndicator = styled.span<{ $asc: boolean }>`
    margin-left: 0.15em;
    font-weight: 700;
    color: #495057;
    font-size: 0.95em;
`;

// Custom table header cell with consistent font styling - more compact
const StandingsTableHeaderCell = styled(BaseTableHeaderCell)`
    font-family: inherit;
    font-weight: 600;
    font-size: 0.8em;
    padding: 0.1em 0.4em;
`;

// Rank header cell with centered content. Fixed 40px width; base TableHeaderCell has :first-child { min-width: 200px } so override it.
const RankHeaderCell = styled(StandingsTableHeaderCell)`
    text-align: center;
    padding: 0.1em 0.2em;
    min-width: 40px !important;
    max-width: 40px;
    width: 40px;
    box-sizing: border-box;
    &:first-child {
        min-width: 40px !important;
        max-width: 40px;
    }
    @media (max-width: 768px) {
        position: sticky;
        left: 0;
        z-index: 2;
        background-color: #f8f9fa;
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.06);
    }
`;

// Custom table cell with consistent font styling - more compact
const StandingsTableCell = styled(BaseTableCell)<{ $isSeparator?: boolean; $afterSeparator?: boolean }>`
    font-family: inherit;
    font-weight: 400;
    font-size: 0.8em;
    padding: 0.1em 0.4em;
    
    &:first-child {
        font-weight: 500;
        background-color: inherit !important;
    }
    
    ${props => props.$isSeparator ? `
        border-right: 3px solid #bbb !important;
    ` : `
        border-right: 1px solid #e9ecef;
    `}
    
    ${props => props.$afterSeparator && `
        border-left: none !important;
    `}
`;

// Team name cell - always left-aligned, prevent wrapping
const TeamNameCell = styled(StandingsTableCell)<{ $isSeparator?: boolean }>`
    justify-content: flex-start !important;
    text-align: left !important;
    white-space: nowrap !important;
    overflow: visible !important;
    width: 100%;
    
    ${props => props.$isSeparator && `
        border-right: 3px solid #bbb !important;
    `}
`;

// Coach cell - left-aligned like team name, uses same separator as Coach header (3px right border). Sticky on mobile.
const CoachCell = styled(StandingsTableCell)<{ $isEven?: boolean }>`
    justify-content: flex-start !important;
    text-align: left !important;
    white-space: nowrap !important;
    @media (max-width: 768px) {
        position: sticky;
        left: var(--sticky-coach-left);
        z-index: 1;
        background-color: ${props => props.$isEven ? '#f8f9fa' : 'white'};
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.06);
    }
`;

// Inner layer for rank cell - guarantees opaque background on mobile (same approach as Coach).
const RankCellBg = styled.div<{ $isEven?: boolean }>`
    display: none;
    @media (max-width: 768px) {
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: ${props => props.$isEven ? '#f8f9fa' : 'white'};
        z-index: 0;
    }
`;

// Rank cell with centered content - completely override first-child styling, compact. Sticky on mobile; opaque via RankCellBg.
const RankCell = styled(BaseTableCell)<{ $isEven?: boolean }>`
    font-family: inherit;
    font-weight: 400;
    font-size: 0.8em;
    text-align: center !important;
    justify-content: center !important;
    background-color: inherit !important;
    border-left: 1px solid #e9ecef;
    padding: 0.1em 0.2em;
    position: relative;
    
    /* Completely override first-child styles */
    &:first-child {
        justify-content: center !important;
        text-align: center !important;
        background-color: inherit !important;
        font-weight: 400 !important;
    }
    
    @media (max-width: 768px) {
        position: sticky;
        left: 0;
        z-index: 2;
        background-color: transparent !important;
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.06);
        & > span {
            position: relative;
            z-index: 1;
        }
    }
`;

// Custom table header cell with separator support
const StandingsTableHeaderCellWithSeparator = styled(StandingsTableHeaderCell)<{ $isSeparator?: boolean }>`
    ${props => props.$isSeparator && `
        border-right: 3px solid #bbb !important;
    `}
`;

// Coach header cell - sticky on mobile so rank + coach stay visible when scrolling through stats.
const CoachHeaderCell = styled(StandingsTableHeaderCellWithSeparator)`
    @media (max-width: 768px) {
        position: sticky;
        left: var(--sticky-coach-left);
        z-index: 1;
        background-color: #f8f9fa;
        box-shadow: 2px 0 6px rgba(0, 0, 0, 0.06);
    }
`;

// Team name text - single line, compact, no wrapping
const TeamNameText = styled.div`
    font-weight: 600;
    font-size: 0.85em;
    color: #333;
    line-height: 1.2;
    white-space: nowrap !important;
    overflow: visible !important;
`;

// Coach name text - no italics, compact
const CoachNameText = styled.div`
    font-size: 0.85em;
    color: #666;
    line-height: 1.2;
`;

// Wrapper for icon + popover so we can position the popover and handle outside click
const RankInfoWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    margin-left: 0.25em;
`;

// Icon button for tie breaker info — minimal chrome so it sits next to team name
const RankInfoButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    color: #6c757d;
    vertical-align: middle;

    &:hover {
        color: #495057;
    }
`;

// Popover shown when icon is pressed
const RankInfoPopover = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0.25em;
    z-index: 10;
    min-width: 220px;
    max-width: 320px;
    padding: 0.6em 0.75em;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 0.8em;
    color: #333;
    white-space: pre-line;
    line-height: 1.4;
`;

// Custom change indicator with consistent font styling (same size/weight as regular cells)
const StandingsChangeIndicator = styled(BaseChangeIndicator)`
    font-family: inherit;
    font-size: 0.85em;
    font-weight: 400;
`;

const WeekSelectorContainer = styled.div`
    margin-bottom: 1em;
    display: flex;
    align-items: center;
    gap: 0.5em;
    flex-wrap: wrap;
`;

const WeekSelectorLabel = styled.label`
    font-weight: 600;
    font-size: 0.9em;
    color: #495057;
`;

const WeekSelectorDropdown = styled.select`
    padding: 0.4em 0.8em;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
    font-family: inherit;
    
    &:hover {
        border-color: #adb5bd;
    }
    
    &:focus {
        outline: none;
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
`;

const WeekPreviewBadge = styled.span`
    padding: 0.2em 0.6em;
    background-color: #fff3cd;
    color: #856404;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
    border: 1px solid #ffc107;
`;

const WeekCompleteBadge = styled.span`
    padding: 0.2em 0.6em;
    background-color: #d4edda;
    color: #155724;
    border-radius: 4px;
    font-size: 0.85em;
    font-weight: 500;
    border: 1px solid #28a745;
`;

/**
 * Playoff-status badge. Three variants, displayed next to the team name:
 *  - "division"   — guaranteed division-winner seat (strongest), shown in solid green.
 *  - "playoff"    — guaranteed *some* playoff seat (wildcard pathway), shown in lighter blue-green.
 *  - "eliminated" — mathematically out of playoff contention, shown in muted red; gated by a toggle.
 */
const ClinchBadge = styled.span<{ $variant: 'division' | 'playoff' | 'eliminated' }>`
    margin-left: 0.35em;
    padding: 0 0.35em;
    border-radius: 3px;
    font-size: 0.7em;
    font-weight: 700;
    letter-spacing: 0.04em;
    vertical-align: middle;
    cursor: help;
    white-space: nowrap;
    ${props => {
        if (props.$variant === 'division') return `
            background-color: #d4edda;
            color: #155724;
            border: 1px solid #28a745;
        `;
        if (props.$variant === 'playoff') return `
            background-color: #d1ecf1;
            color: #0c5460;
            border: 1px solid #17a2b8;
        `;
        return `
            background-color: #f8d7da;
            color: #721c24;
            border: 1px solid #dc3545;
        `;
    }}
`;

const DownloadButton = styled.button`
    padding: 0.4em 0.8em;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.9em;
    cursor: pointer;
    font-family: inherit;
    font-weight: 500;
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: #218838;
    }
    
    &:active {
        background-color: #1e7e34;
    }
    
    &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
`;

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.4em 0.8em;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
`;

const SortControlsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75em;
    margin-bottom: 0.75em;
    flex-wrap: wrap;
`;

const SortLabel = styled.span`
    font-weight: 600;
    font-size: 0.9em;
    color: #495057;
`;

const SortButton = styled.button<{ $active?: boolean }>`
    padding: 0.35em 0.65em;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.85em;
    background-color: ${props => props.$active ? '#e7f1ff' : 'white'};
    color: ${props => props.$active ? '#004085' : '#495057'};
    cursor: pointer;
    font-family: inherit;

    &:hover {
        background-color: ${props => props.$active ? '#cce5ff' : '#f8f9fa'};
    }
`;

const SortColumnSelect = styled.select`
    padding: 0.35em 0.65em;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.85em;
    background-color: white;
    cursor: pointer;
    font-family: inherit;
`;

const ToggleLabel = styled.label`
    font-size: 0.9em;
    color: #495057;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5em;
    user-select: none;
`;

const ToggleSwitch = styled.input`
    width: 40px;
    height: 20px;
    cursor: pointer;
`;

interface SeasonStandingsProps {
    seasonData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
        playoffStructure?: PlayoffStructure;
    };
    leagueCoaches: CoachModel[];
    /** Shared week selection; when changed, all section dropdowns stay in sync. */
    selectedWeekIndex: number;
    onSelectedWeekChange: (index: number) => void;
}

// Tie breaker order (must match sort in getStandingsThroughWeek) — used for within-division ranking
const TIE_BREAKER_LABELS: { key: keyof TeamStats; label: string }[] = [
    { key: 'points', label: 'Points' },
    { key: 'divisionPoints', label: 'Division Points' },
    { key: 'wins', label: 'Wins' },
    { key: 'diff', label: 'TD differential' },
    { key: 'touchdownsFor', label: 'Touchdowns for' },
    { key: 'casualtiesFor', label: 'Casualties' }
];

/** League-wide tie breaker order (no division points — cross-division comparisons; same-division pairs use {@link TIE_BREAKER_LABELS}). */
const TIE_BREAKER_LABELS_LEAGUE_WIDE: { key: keyof TeamStats; label: string }[] = [
    { key: 'points', label: 'Points' },
    { key: 'wins', label: 'Wins' },
    { key: 'diff', label: 'TD differential' },
    { key: 'touchdownsFor', label: 'Touchdowns for' },
    { key: 'casualtiesFor', label: 'Casualties' }
];

export type StandingsSortMode = 'byDivision' | 'leagueWide' | 'byColumn' | 'playoffPicture';

/** Sort key for "By column" mode: TeamStats field or computed 'casDiff'. */
export type StandingsSortColumnKey = keyof TeamStats | 'casDiff';

type TieBreakerLabelDef = { key: keyof TeamStats; label: string };

function playoffPicturePairTieBreakerLabels(above: TeamStats, below: TeamStats): TieBreakerLabelDef[] {
    return above.divisionName === below.divisionName ? TIE_BREAKER_LABELS : TIE_BREAKER_LABELS_LEAGUE_WIDE;
}

/** Base sort columns in same order as table: Pts, Div Pts, W, T, L, TF, TA, Diff, Cas, Cas Against, Cas diff. */
const SORTABLE_COLUMNS_BASE: { key: StandingsSortColumnKey; label: string }[] = [
    { key: 'points', label: 'Pts' },
    { key: 'divisionPoints', label: 'Div Pts' },
    { key: 'wins', label: 'W' },
    { key: 'ties', label: 'T' },
    { key: 'losses', label: 'L' },
    { key: 'touchdownsFor', label: 'TF' },
    { key: 'touchdownsAgainst', label: 'TA' },
    { key: 'diff', label: 'Diff' },
    { key: 'casualtiesFor', label: 'Cas' },
    { key: 'casualtiesAgainst', label: 'Cas Against' },
    { key: 'casDiff', label: 'Cas diff' },
];

/** Extended stat columns (table order), shown only when "Show extended stats" is on. */
const SORTABLE_COLUMNS_EXTENDED: { key: keyof TeamStats; label: string }[] = [
    { key: 'kills', label: 'Kills' },
    { key: 'blocks', label: 'Blocks' },
    { key: 'avBr', label: 'AvBr' },
    { key: 'ko', label: 'KO' },
    { key: 'surfs', label: 'Surfs' },
    { key: 'fouls', label: 'Fouls' },
    { key: 'passes', label: 'Passes' },
    { key: 'spp', label: 'SPP' },
];

/** Extended stat header labels and keys for "my stats" vs "against". */
const EXTENDED_STAT_HEADERS: { key: keyof TeamStats; againstKey: keyof TeamStats; label: string; againstLabel: string }[] = [
    { key: 'kills', againstKey: 'killsAgainst', label: 'Kills', againstLabel: 'Kills against' },
    { key: 'blocks', againstKey: 'blocksAgainst', label: 'Blocks', againstLabel: 'Blocks against' },
    { key: 'avBr', againstKey: 'avBrAgainst', label: 'AvBr', againstLabel: 'AvBr against' },
    { key: 'ko', againstKey: 'koAgainst', label: 'KO', againstLabel: 'KO against' },
    { key: 'surfs', againstKey: 'surfsAgainst', label: 'Surfs', againstLabel: 'Surfs against' },
    { key: 'fouls', againstKey: 'foulsAgainst', label: 'Fouls', againstLabel: 'Fouls against' },
    { key: 'passes', againstKey: 'passesAgainst', label: 'Passes', againstLabel: 'Passes against' },
    { key: 'spp', againstKey: 'sppAgainst', label: 'SPP', againstLabel: 'SPP against' },
];

export interface RankReason {
    /** First tie breaker that decided this rank vs the team below (or null if last) */
    decidedBy: string | null;
    /** Tooltip text: "Rank N — Decided by: X" (empty for last in division) */
    tooltip: string;
}

/** Given a sorted division list, return rank reason for each team (why they are above the next). */
function getRankReasons(
    divisionTeams: TeamStats[],
    tieBreakerLabels: TieBreakerLabelDef[] | ((above: TeamStats, below: TeamStats) => TieBreakerLabelDef[]) = TIE_BREAKER_LABELS,
    rankLabel: string = 'Rank'
): RankReason[] {
    const reasons: RankReason[] = [];
    for (let i = 0; i < divisionTeams.length; i++) {
        const team = divisionTeams[i];
        const next = divisionTeams[i + 1];
        if (!next) {
            reasons.push({ decidedBy: null, tooltip: '' });
            continue;
        }
        const labels = typeof tieBreakerLabels === 'function' ? tieBreakerLabels(team, next) : tieBreakerLabels;
        let decidedBy: string | null = null;
        for (const { key, label } of labels) {
            const a = team[key];
            const b = next[key];
            const aVal = typeof a === 'number' ? a : 0;
            const bVal = typeof b === 'number' ? b : 0;
            if (aVal !== bVal) {
                const aD = key === 'diff' ? (aVal > 0 ? `+${aVal}` : String(aVal)) : String(aVal);
                const bD = key === 'diff' ? (bVal > 0 ? `+${bVal}` : String(bVal)) : String(bVal);
                decidedBy = `${label} (${aD} vs ${bD})`;
                break;
            }
        }
        if (!decidedBy) decidedBy = 'Tied on all tie breakers';
        reasons.push({
            decidedBy,
            tooltip: `${rankLabel} ${i + 1} — Decided by: ${decidedBy}`
        });
    }
    return reasons;
}

/** Returns the tiebreaker reason why team "above" is ranked above "below", or null if tied on all. */
function getReasonBetween(
    above: TeamStats,
    below: TeamStats,
    tieBreakerLabels: TieBreakerLabelDef[] | ((a: TeamStats, b: TeamStats) => TieBreakerLabelDef[]),
    tooltipPrefix: string
): RankReason | null {
    const labels = typeof tieBreakerLabels === 'function' ? tieBreakerLabels(above, below) : tieBreakerLabels;
    let decidedBy: string | null = null;
    for (const { key, label } of labels) {
        const a = above[key];
        const b = below[key];
        const aVal = typeof a === 'number' ? a : 0;
        const bVal = typeof b === 'number' ? b : 0;
        if (aVal !== bVal) {
            const aD = key === 'diff' ? (aVal > 0 ? `+${aVal}` : String(aVal)) : String(aVal);
            const bD = key === 'diff' ? (bVal > 0 ? `+${bVal}` : String(bVal)) : String(bVal);
            decidedBy = `${label} (${aD} vs ${bD})`;
            break;
        }
    }
    if (!decidedBy) return null;
    return {
        decidedBy,
        tooltip: `${tooltipPrefix}${decidedBy} (vs first team out)`
    };
}

export const SeasonStandings: React.FC<SeasonStandingsProps> = ({
    seasonData,
    leagueCoaches,
    selectedWeekIndex,
    onSelectedWeekChange
}) => {
    const [showExtendedStats, setShowExtendedStats] = useState(false);
    const [showExtendedAgainst, setShowExtendedAgainst] = useState(false);
    // Default off: mathematically-eliminated teams are hidden so we don't rub it in.
    // The toggle is an opt-in for viewers who want the full playoff picture.
    const [showEliminated, setShowEliminated] = useState(false);
    const [rankInfoOpen, setRankInfoOpen] = useState<{ divisionName: string; teamId: string } | null>(null);
    const rankInfoWrapperRef = useRef<HTMLDivElement | null>(null);

    const [sortMode, setSortMode] = useState<StandingsSortMode>('byDivision');
    const [sortColumnKey, setSortColumnKey] = useState<StandingsSortColumnKey>('points');
    const [sortAsc, setSortAsc] = useState(false);
    const [selectedBumpDivision, setSelectedBumpDivision] = useState<string>("");

    // Sort options: base columns in table order, plus extended stats when toggle is on.
    // When "Show stats against" is on, extended options use against keys/labels so sort-by-column matches displayed columns.
    const sortOptions = useMemo(() => {
        const base = [...SORTABLE_COLUMNS_BASE];
        if (showExtendedStats) {
            const extended = showExtendedAgainst
                ? EXTENDED_STAT_HEADERS.map(({ againstKey, againstLabel }) => ({ key: againstKey, label: againstLabel }))
                : SORTABLE_COLUMNS_EXTENDED;
            return base.concat(extended);
        }
        return base;
    }, [showExtendedStats, showExtendedAgainst]);

    // When extended stats is turned off, if current sort column is extended, reset to points
    useEffect(() => {
        if (!showExtendedStats && sortColumnKey !== 'casDiff' && SORTABLE_COLUMNS_EXTENDED.some(c => c.key === sortColumnKey)) {
            setSortColumnKey('points');
        }
    }, [showExtendedStats, sortColumnKey]);

    // When "Show stats against" is toggled, keep sort selection valid: map between for/against keys so sort matches display
    useEffect(() => {
        if (!showExtendedStats) return;
        const againstKey = EXTENDED_STAT_HEADERS.find(h => h.againstKey === sortColumnKey);
        const forKey = EXTENDED_STAT_HEADERS.find(h => h.key === sortColumnKey);
        if (!showExtendedAgainst && againstKey) {
            setSortColumnKey(againstKey.key);
        } else if (showExtendedAgainst && forKey) {
            setSortColumnKey(forKey.againstKey);
        }
    }, [showExtendedAgainst, showExtendedStats]);

    // Close rank info popover on Escape or click outside
    useEffect(() => {
        if (!rankInfoOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setRankInfoOpen(null);
        };
        const onDocClick = (e: MouseEvent) => {
            if (rankInfoWrapperRef.current && !rankInfoWrapperRef.current.contains(e.target as Node)) {
                setRankInfoOpen(null);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("click", onDocClick, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("click", onDocClick, true);
        };
    }, [rankInfoOpen]);

    // Validate and normalize data structure
    const safeSeasonData = React.useMemo(() => {
        if (!seasonData) {
            return { divisions: [], weeks: [], playoffStructure: undefined };
        }
        const ps = seasonData.playoffStructure;
        const validPlayoff = ps && typeof ps.divisionWinnersPerDivision === 'number' && typeof ps.wildcardCount === 'number' && typeof ps.byeCount === 'number'
            ? ps
            : undefined;
        return {
            divisions: Array.isArray(seasonData.divisions) ? seasonData.divisions : [],
            weeks: Array.isArray(seasonData.weeks) ? seasonData.weeks : [],
            playoffStructure: validPlayoff
        };
    }, [seasonData]);

    const safeLeagueCoaches = React.useMemo(() => {
        return Array.isArray(leagueCoaches) ? leagueCoaches : [];
    }, [leagueCoaches]);

    const divisionNames = useMemo(() => safeSeasonData.divisions.map(d => d.divisionName).filter(Boolean), [safeSeasonData.divisions]);
    useEffect(() => {
        if (divisionNames.length > 0 && (!selectedBumpDivision || !divisionNames.includes(selectedBumpDivision))) {
            setSelectedBumpDivision(divisionNames[0]);
        }
    }, [divisionNames, selectedBumpDivision]);

    // Find the last complete week index
    const lastCompleteWeekIndex = useMemo(() => {
        let lastComplete = -1;
        safeSeasonData.weeks.forEach((week, index) => {
            if (isRegularSeasonWeekName(week?.weekName || '') && isWeekComplete(week)) {
                lastComplete = index;
            }
        });
        return lastComplete;
    }, [safeSeasonData.weeks]);

    // Update shared week selection when lastCompleteWeekIndex advances (e.g., when new data loads)
    // Only auto-update if currently showing a week that's now complete (to show the newest complete week)
    React.useEffect(() => {
        if (lastCompleteWeekIndex >= 0 && lastCompleteWeekIndex > selectedWeekIndex) {
            const currentWeek = safeSeasonData.weeks[selectedWeekIndex];
            if (currentWeek && isWeekComplete(currentWeek)) {
                onSelectedWeekChange(lastCompleteWeekIndex);
            }
        }
    }, [lastCompleteWeekIndex, selectedWeekIndex, safeSeasonData.weeks, onSelectedWeekChange]);

    // Validate data structure
    if (!seasonData) {
        const logger = createComponentLogger('SeasonStandings', 'validateData');
        logger.error('Invalid season data structure', { seasonData });
        return (
            <StandingsSection>
                <div data-testid="error">Error: Invalid season data structure</div>
            </StandingsSection>
        );
    }

    // Get regular season weeks for the dropdown
    const regularSeasonWeeks = useMemo(() => {
        return safeSeasonData.weeks
            .map((week, index) => ({ week, index }))
            .filter(({ week }) => week && isRegularSeasonWeekName(week.weekName || ''));
    }, [safeSeasonData.weeks]);

    // Calculate standings for the selected week (by-division data)
    const standings = useMemo(() => {
        return getStandingsThroughWeek(safeSeasonData, safeLeagueCoaches, selectedWeekIndex);
    }, [selectedWeekIndex, safeSeasonData.weeks, safeSeasonData.divisions, safeLeagueCoaches]);

    // Flatten all teams from standings (for league-wide and by-column views)
    const allTeamsFlat = useMemo(() => {
        return Object.values(standings).flat();
    }, [standings]);

    // Map teamId → playoff status (clinch or elimination). See utils/playoffClinch.ts for details.
    const clinchStatuses = useMemo<Record<string, ClinchReason>>(() => {
        return getClinchStatuses(standings, safeSeasonData, safeSeasonData.playoffStructure);
    }, [standings, safeSeasonData]);

    const hasAnyClinch = useMemo(
        () => Object.values(clinchStatuses).some(r => r.status === 'division' || r.status === 'playoff'),
        [clinchStatuses]
    );
    const hasAnyElimination = useMemo(
        () => Object.values(clinchStatuses).some(r => r.status === 'eliminated'),
        [clinchStatuses]
    );

    const renderClinchBadge = (teamId: string) => {
        const reason = clinchStatuses[teamId];
        if (!reason) return null;
        // Mirrors the NFL scoreboard convention: 'y' = clinched division, 'x' = clinched playoff spot, 'e' = eliminated.
        // Elimination is gated behind a toggle so we don't rub it in by default.
        if (reason.status === 'eliminated' && !showEliminated) return null;
        const label = reason.status === 'division' ? 'y' : reason.status === 'playoff' ? 'x' : 'e';
        return (
            <ClinchBadge
                $variant={reason.status}
                title={reason.message}
                aria-label={reason.message}
                data-testid={`clinch-badge-${teamId}`}
                data-clinch-status={reason.status}
            >
                {label}
            </ClinchBadge>
        );
    };

    // League-wide tab: single list sorted by league-wide tiebreakers only (no division points), with rank reasons
    const flatTeamsLeagueWide = useMemo(() => {
        const list = [...allTeamsFlat];
        list.sort(compareByTieBreakerLeagueWide);
        return list;
    }, [allTeamsFlat]);

    const leagueWideRankReasons = useMemo(
        () => getRankReasons(flatTeamsLeagueWide, TIE_BREAKER_LABELS_LEAGUE_WIDE),
        [flatTeamsLeagueWide]
    );

    /** Playoff picture if season has playoff structure: division winners, wildcards, remaining (playoff-pool ordering — division points when both teams are in the same division). */
    const playoffPictureGroups = useMemo((): { divisionWinners: TeamStats[]; wildcards: TeamStats[]; remaining: TeamStats[] } | null => {
        const ps = safeSeasonData.playoffStructure;
        if (!ps || standings == null) return null;
        const perDiv = Math.max(0, ps.divisionWinnersPerDivision);
        const wildcardCount = Math.max(0, ps.wildcardCount);
        const divisionWinners: TeamStats[] = [];
        Object.values(standings).forEach(divisionTeams => {
            for (let i = 0; i < perDiv && i < divisionTeams.length; i++) {
                divisionWinners.push(divisionTeams[i]);
            }
        });
        divisionWinners.sort(compareByTieBreakerPlayoffPool);
        const winnerIds = new Set(divisionWinners.map(t => t.teamId));
        const nonWinners = allTeamsFlat.filter(t => !winnerIds.has(t.teamId));
        nonWinners.sort(compareByTieBreakerPlayoffPool);
        const wildcards = nonWinners.slice(0, wildcardCount);
        const remaining = nonWinners.slice(wildcardCount);
        return { divisionWinners, wildcards, remaining };
    }, [standings, allTeamsFlat, safeSeasonData.playoffStructure]);

    /** Rank reasons for playoff picture sections (why each seed is above the next); uses "Seed" in tooltips. */
    const playoffPictureRankReasons = useMemo(() => {
        if (!playoffPictureGroups) return null;
        const divisionWinnerReasons = getRankReasons(playoffPictureGroups.divisionWinners, playoffPicturePairTieBreakerLabels, 'Seed');
        const wildcardReasons = getRankReasons(playoffPictureGroups.wildcards, playoffPicturePairTieBreakerLabels, 'Seed');
        const remainingReasons = getRankReasons(playoffPictureGroups.remaining, playoffPicturePairTieBreakerLabels, 'Rank');
        const lastWildcard = playoffPictureGroups.wildcards.length > 0 && playoffPictureGroups.remaining.length > 0
            ? getReasonBetween(
                playoffPictureGroups.wildcards[playoffPictureGroups.wildcards.length - 1],
                playoffPictureGroups.remaining[0],
                playoffPicturePairTieBreakerLabels,
                `Seed ${playoffPictureGroups.divisionWinners.length + playoffPictureGroups.wildcards.length} — Decided by: `
            )
            : null;
        return {
            divisionWinnerReasons,
            wildcardReasons,
            remainingReasons,
            lastWildcardReason: lastWildcard
        };
    }, [playoffPictureGroups]);

    // By-column: sort by selected column and direction
    const flatTeamsByColumn = useMemo(() => {
        const list = [...allTeamsFlat];
        const key = sortColumnKey;
        const asc = sortAsc;
        list.sort((a, b) => {
            let aVal: number | string;
            let bVal: number | string;
            if (key === 'casDiff') {
                aVal = a.casualtiesFor - a.casualtiesAgainst;
                bVal = b.casualtiesFor - b.casualtiesAgainst;
            } else {
                aVal = a[key as keyof TeamStats] ?? 0;
                bVal = b[key as keyof TeamStats] ?? 0;
            }
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return asc ? aVal - bVal : bVal - aVal;
            }
            const aStr = String(aVal);
            const bStr = String(bVal);
            return asc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        });
        return list;
    }, [allTeamsFlat, sortColumnKey, sortAsc]);

    // Get the selected week info for display
    const selectedWeekInfo = useMemo(() => {
        const week = safeSeasonData.weeks[selectedWeekIndex];
        if (!week) return null;
        const isComplete = isWeekComplete(week);
        const completed = week.matches?.filter(m => m?.results).length || 0;
        const total = week.matches?.length || 0;
        return { week, isComplete, completed, total };
    }, [selectedWeekIndex, safeSeasonData.weeks]);

    // Bump chart data: rank per week (only when By Division or League-wide; not for By stat).
    // Only show weeks that have at least one completed match in that week; rank uses standings through that week.
    const bumpChartData = useMemo(() => {
        if (sortMode === 'byColumn' || regularSeasonWeeks.length === 0) return null;
        const weeks = regularSeasonWeeks;
        let firstIndexWithData = -1;
        let lastIndexWithData = -1;
        for (let i = 0; i < weeks.length; i++) {
            const week = weeks[i].week;
            const hasResults = week?.matches?.some((m: { results?: unknown }) => m?.results != null) ?? false;
            if (hasResults) {
                if (firstIndexWithData < 0) firstIndexWithData = i;
                lastIndexWithData = i;
            }
        }
        if (firstIndexWithData < 0 || lastIndexWithData < firstIndexWithData) return null;
        const chartWeeks = weeks.slice(firstIndexWithData, lastIndexWithData + 1);
        const weekNames = chartWeeks.map(w => w.week.weekName || `Week ${w.index + 1}`);

        if (sortMode === 'byDivision') {
            const divisionName = selectedBumpDivision;
            const divisionStandingsByWeek = chartWeeks.map(w => (getStandingsThroughWeek(safeSeasonData, safeLeagueCoaches, w.index)[divisionName] || []) as TeamStats[]);
            const uniqueTeams = Array.from(new Map(divisionStandingsByWeek.flat().map(t => [t.teamId, t])).values());
            const series: BumpSeries[] = uniqueTeams.map((team, idx) => ({
                teamId: team.teamId,
                coachName: `${team.teamName} - ${team.coachName}`,
                color: BUMP_CHART_COLORS[idx % BUMP_CHART_COLORS.length],
                points: chartWeeks.map((_, wi) => {
                    const list = divisionStandingsByWeek[wi] || [];
                    const rank = list.findIndex((t: TeamStats) => t.teamId === team.teamId) + 1;
                    return { x: wi, y: rank || list.length };
                })
            }));
            return { weekNames, series, title: `${divisionName} — rank over time` };
        } else {
            const flatByWeek = chartWeeks.map(w => {
                const divStandings = getStandingsThroughWeek(safeSeasonData, safeLeagueCoaches, w.index);
                const flat = Object.values(divStandings).flat() as TeamStats[];
                flat.sort(compareByTieBreakerWithinDivision);
                return flat;
            });
            const allTeamIds = Array.from(new Set(flatByWeek.flat().map(t => t.teamId)));
            const series: BumpSeries[] = allTeamIds.map((teamId, idx) => {
                const team = flatByWeek.flat().find(t => t.teamId === teamId)!;
                return {
                    teamId,
                    coachName: `${team.teamName} - ${team.coachName}`,
                    color: BUMP_CHART_COLORS[idx % BUMP_CHART_COLORS.length],
                    points: chartWeeks.map((_, wi) => {
                        const list = flatByWeek[wi] || [];
                        const rank = list.findIndex(t => t.teamId === teamId) + 1;
                        return { x: wi, y: rank || list.length };
                    })
                };
            });
            return { weekNames, series, title: 'League-wide — rank over time' };
        }
    }, [sortMode, selectedBumpDivision, regularSeasonWeeks, safeSeasonData.weeks, safeSeasonData.divisions, safeLeagueCoaches]);

    // Convert standings to CSV format
    const convertStandingsToCSV = (): string => {
        const headers = [
            'Division',
            'Rank',
            'Team',
            'Coach',
            'GP',
            'Pts',
            'Div Pts',
            'W',
            'T',
            'L',
            'TF',
            'TA',
            'Diff',
            'Cas',
            'Cas Against',
            'Cas Diff',
            ...(showExtendedStats ? EXTENDED_STAT_HEADERS.map(h => showExtendedAgainst ? h.againstLabel : h.label) : [])
        ];

        const rows: string[] = [headers.join(',')];

        Object.entries(standings).forEach(([divisionName, divisionTeams]) => {
            divisionTeams.forEach((team, index) => {
                const rank = index + 1;
                const casDiff = team.casualtiesFor - team.casualtiesAgainst;
                
                // Escape commas and quotes in values
                const escapeCSV = (value: string | number): string => {
                    const str = String(value);
                    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                        return `"${str.replace(/"/g, '""')}"`;
                    }
                    return str;
                };

                const row = [
                    escapeCSV(divisionName),
                    rank,
                    escapeCSV(team.teamName),
                    escapeCSV(team.coachName),
                    team.gamesPlayed,
                    team.points,
                    team.divisionPoints,
                    team.wins,
                    team.ties,
                    team.losses,
                    team.touchdownsFor,
                    team.touchdownsAgainst,
                    team.diff,
                    team.casualtiesFor,
                    team.casualtiesAgainst,
                    casDiff,
                    ...(showExtendedStats ? EXTENDED_STAT_HEADERS.map(({ key, againstKey }) =>
                        showExtendedAgainst ? (team[againstKey] ?? 0) : (team[key] ?? 0)
                    ) : [])
                ].join(',');

                rows.push(row);
            });
        });

        return rows.join('\n');
    };

    // Handle CSV download
    const handleDownloadCSV = () => {
        const csvContent = convertStandingsToCSV();
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        // Generate filename with week name
        const weekName = selectedWeekInfo?.week.weekName || 'Standings';
        const filename = `${weekName.replace(/[^a-z0-9]/gi, '_')}_Standings.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <StandingsSection>
            {safeSeasonData.divisions.length > 0 && safeSeasonData.weeks.length > 0 ? (
                <>
                    <WeekSelectorContainer>
                        <WeekSelectorLabel htmlFor="week-selector">View Standings As Of:</WeekSelectorLabel>
                        <WeekSelectorDropdown
                            id="week-selector"
                            value={selectedWeekIndex}
                            onChange={(e) => {
                                const newIndex = parseInt(e.target.value, 10);
                                if (!isNaN(newIndex) && newIndex >= 0) {
                                    onSelectedWeekChange(newIndex);
                                }
                            }}
                        >
                            {regularSeasonWeeks.map(({ week, index }) => {
                                const isComplete = isWeekComplete(week);
                                const completed = week.matches?.filter(m => m?.results).length || 0;
                                const total = week.matches?.length || 0;
                                return (
                                    <option key={index} value={index}>
                                        {week.weekName} {isComplete ? '(Complete)' : `(${completed}/${total} matches)`}
                                    </option>
                                );
                            })}
                        </WeekSelectorDropdown>
                        {selectedWeekInfo && (
                            <>
                                {selectedWeekInfo.isComplete ? (
                                    <WeekCompleteBadge>Complete Week</WeekCompleteBadge>
                                ) : (
                                    <WeekPreviewBadge>
                                        Preview: {selectedWeekInfo.completed}/{selectedWeekInfo.total} matches
                                    </WeekPreviewBadge>
                                )}
                            </>
                        )}
                        <DownloadButton onClick={handleDownloadCSV}>
                            📥 Download CSV
                        </DownloadButton>
                        <ToggleContainer>
                            <ToggleLabel>
                                <ToggleSwitch
                                    type="checkbox"
                                    checked={showExtendedStats}
                                    onChange={(e) => setShowExtendedStats(e.target.checked)}
                                />
                                Show Extended Stats
                            </ToggleLabel>
                        </ToggleContainer>
                        {showExtendedStats && (
                            <ToggleContainer>
                                <ToggleLabel>
                                    <ToggleSwitch
                                        type="checkbox"
                                        checked={showExtendedAgainst}
                                        onChange={(e) => setShowExtendedAgainst(e.target.checked)}
                                        data-testid="show-extended-against"
                                    />
                                    Show stats against
                                </ToggleLabel>
                            </ToggleContainer>
                        )}
                        {hasAnyElimination && (
                            <ToggleContainer>
                                <ToggleLabel>
                                    <ToggleSwitch
                                        type="checkbox"
                                        checked={showEliminated}
                                        onChange={(e) => setShowEliminated(e.target.checked)}
                                        data-testid="show-eliminated"
                                    />
                                    Show eliminated teams
                                </ToggleLabel>
                            </ToggleContainer>
                        )}
                    </WeekSelectorContainer>
                    <SortControlsRow>
                        <SortLabel>Sort:</SortLabel>
                        <SortButton
                            type="button"
                            $active={sortMode === 'byDivision'}
                            onClick={() => setSortMode('byDivision')}
                            data-testid="sort-by-division"
                        >
                            By Division
                        </SortButton>
                        <SortButton
                            type="button"
                            $active={sortMode === 'leagueWide'}
                            onClick={() => setSortMode('leagueWide')}
                            data-testid="sort-league-wide"
                        >
                            League-wide
                        </SortButton>
                        <SortButton
                            type="button"
                            $active={sortMode === 'byColumn'}
                            onClick={() => setSortMode('byColumn')}
                            data-testid="sort-by-column"
                        >
                            By stat
                        </SortButton>
                        <SortButton
                            type="button"
                            $active={sortMode === 'playoffPicture'}
                            onClick={() => setSortMode('playoffPicture')}
                            data-testid="sort-playoff-picture"
                        >
                            Playoff picture
                        </SortButton>
                        {sortMode === 'byColumn' && (
                            <>
                                <SortColumnSelect
                                    value={sortColumnKey}
                                    onChange={(e) => setSortColumnKey(e.target.value as StandingsSortColumnKey)}
                                    data-testid="sort-column-select"
                                >
                                    {sortOptions.map(({ key, label }) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </SortColumnSelect>
                                <SortButton
                                    type="button"
                                    $active={true}
                                    onClick={() => setSortAsc(a => !a)}
                                    data-testid="sort-direction"
                                >
                                    {sortAsc ? '↑ Asc' : '↓ Desc'}
                                </SortButton>
                            </>
                        )}
                    </SortControlsRow>
                    {(hasAnyClinch || (hasAnyElimination && showEliminated)) && (
                        <div
                            data-testid="clinch-legend"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.6em',
                                marginBottom: '0.5em',
                                fontSize: '0.8em',
                                color: '#495057'
                            }}
                        >
                            {hasAnyClinch && (
                                <>
                                    <span>Clinched:</span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35em' }}>
                                        <ClinchBadge $variant="division" as="span">y</ClinchBadge>
                                        <span>division</span>
                                    </span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35em' }}>
                                        <ClinchBadge $variant="playoff" as="span">x</ClinchBadge>
                                        <span>playoff spot</span>
                                    </span>
                                </>
                            )}
                            {hasAnyElimination && showEliminated && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35em' }}>
                                    <ClinchBadge $variant="eliminated" as="span">e</ClinchBadge>
                                    <span>eliminated</span>
                                </span>
                            )}
                        </div>
                    )}
                    <TableContainer>
                        <StandingsTableGrid
                            $showExtended={showExtendedStats}
                            $showDivisionColumn={sortMode === 'leagueWide' || sortMode === 'byColumn' || sortMode === 'playoffPicture'}
                        >
                        <TableHeader>
                            <RankHeaderCell>{sortMode === 'playoffPicture' ? 'Seed' : 'Rank'}</RankHeaderCell>
                            {(sortMode === 'leagueWide' || sortMode === 'byColumn' || sortMode === 'playoffPicture') && (
                                <StandingsTableHeaderCell>Division</StandingsTableHeaderCell>
                            )}
                            <StandingsTableHeaderCell>Team</StandingsTableHeaderCell>
                            <CoachHeaderCell $isSeparator>Coach</CoachHeaderCell>
                            <StandingsTableHeaderCell>
                                GP{sortMode === 'byColumn' && sortColumnKey === 'gamesPlayed' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCell>
                                Pts{sortMode === 'byColumn' && sortColumnKey === 'points' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCellWithSeparator $isSeparator>
                                Div Pts{sortMode === 'byColumn' && sortColumnKey === 'divisionPoints' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCellWithSeparator>
                            <StandingsTableHeaderCell>
                                W{sortMode === 'byColumn' && sortColumnKey === 'wins' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCell>
                                T{sortMode === 'byColumn' && sortColumnKey === 'ties' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCellWithSeparator $isSeparator>
                                L{sortMode === 'byColumn' && sortColumnKey === 'losses' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCellWithSeparator>
                            <StandingsTableHeaderCell>
                                TF{sortMode === 'byColumn' && sortColumnKey === 'touchdownsFor' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCell>
                                TA{sortMode === 'byColumn' && sortColumnKey === 'touchdownsAgainst' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCellWithSeparator $isSeparator>
                                Diff{sortMode === 'byColumn' && sortColumnKey === 'diff' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCellWithSeparator>
                            <StandingsTableHeaderCell>
                                Cas{sortMode === 'byColumn' && sortColumnKey === 'casualtiesFor' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCell>
                                Cas Against{sortMode === 'byColumn' && sortColumnKey === 'casualtiesAgainst' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCell>
                            <StandingsTableHeaderCellWithSeparator $isSeparator>
                                Cas diff{sortMode === 'byColumn' && sortColumnKey === 'casDiff' && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                            </StandingsTableHeaderCellWithSeparator>
                            {showExtendedStats && EXTENDED_STAT_HEADERS.map(({ key, againstKey, label, againstLabel }) => (
                                <StandingsTableHeaderCell key={key}>
                                    {showExtendedAgainst ? againstLabel : label}
                                    {sortMode === 'byColumn' && sortColumnKey === (showExtendedAgainst ? againstKey : key) && <SortIndicator $asc={sortAsc}>{sortAsc ? ' ↑' : ' ↓'}</SortIndicator>}
                                </StandingsTableHeaderCell>
                            ))}
                        </TableHeader>
                        
                        {sortMode === 'byDivision' && Object.entries(standings).map(([divisionName, divisionTeams]) => {
                            const rankReasons = getRankReasons(divisionTeams);
                            return (
                            <React.Fragment key={divisionName}>
                                <StandingsDivisionHeaderCell>
                                    {divisionName}
                                </StandingsDivisionHeaderCell>
                                <StandingsDivisionHeaderSpacer />
                                {divisionTeams.map((team, index) => {
                                    const casDiff = team.casualtiesFor - team.casualtiesAgainst;
                                    const rank = index + 1;
                                    const reason = rankReasons[index];
                                    return (
                                        <StandingsTableRow key={team.teamId} $isEven={index % 2 === 0}>
                                            <RankCell $isEven={index % 2 === 0}><RankCellBg $isEven={index % 2 === 0} /><span>{rank}</span></RankCell>
                                            <TeamNameCell>
                                                <TeamNameText>{team.teamName}{renderClinchBadge(team.teamId)}</TeamNameText>
                                                {reason && reason.decidedBy != null && (
                                                    <RankInfoWrapper
                                                        ref={
                                                            rankInfoOpen?.divisionName === divisionName &&
                                                            rankInfoOpen?.teamId === team.teamId
                                                                ? rankInfoWrapperRef
                                                                : undefined
                                                        }
                                                    >
                                                        <RankInfoButton
                                                            type="button"
                                                            title={reason.tooltip}
                                                            aria-label={`Why ${team.teamName} is rank ${rank}`}
                                                            aria-expanded={
                                                                rankInfoOpen?.divisionName === divisionName &&
                                                                rankInfoOpen?.teamId === team.teamId
                                                            }
                                                            onClick={() =>
                                                                setRankInfoOpen((prev) =>
                                                                    prev?.divisionName === divisionName &&
                                                                    prev?.teamId === team.teamId
                                                                        ? null
                                                                        : { divisionName, teamId: team.teamId }
                                                                )
                                                            }
                                                        >
                                                            <InfoIcon size="0.85em" />
                                                        </RankInfoButton>
                                                        {rankInfoOpen?.divisionName === divisionName &&
                                                            rankInfoOpen?.teamId === team.teamId && (
                                                                <RankInfoPopover>{reason.tooltip}</RankInfoPopover>
                                                            )}
                                                    </RankInfoWrapper>
                                                )}
                                            </TeamNameCell>
                                            <CoachCell $isSeparator $isEven={index % 2 === 0}>
                                                <CoachNameText>{team.coachName}</CoachNameText>
                                            </CoachCell>
                                            <StandingsTableCell $afterSeparator>{team.gamesPlayed}</StandingsTableCell>
                                            <StandingsTableCell>{team.points}</StandingsTableCell>
                                            <StandingsTableCell $isSeparator>{team.divisionPoints}</StandingsTableCell>
                                            <StandingsTableCell $afterSeparator>{team.wins}</StandingsTableCell>
                                            <StandingsTableCell>{team.ties}</StandingsTableCell>
                                            <StandingsTableCell $isSeparator>{team.losses}</StandingsTableCell>
                                            <StandingsTableCell $afterSeparator>{team.touchdownsFor}</StandingsTableCell>
                                            <StandingsTableCell>{team.touchdownsAgainst}</StandingsTableCell>
                                            <StandingsTableCell $isSeparator>
                                                <StandingsChangeIndicator $isPositive={team.diff > 0} $isNegative={team.diff < 0}>
                                                    {team.diff > 0 ? '+' : ''}{team.diff}
                                                </StandingsChangeIndicator>
                                            </StandingsTableCell>
                                            <StandingsTableCell $afterSeparator>{team.casualtiesFor}</StandingsTableCell>
                                            <StandingsTableCell>{team.casualtiesAgainst}</StandingsTableCell>
                                            <StandingsTableCell $isSeparator>
                                                <StandingsChangeIndicator $isPositive={casDiff > 0} $isNegative={casDiff < 0}>
                                                    {casDiff > 0 ? '+' : ''}{casDiff}
                                                </StandingsChangeIndicator>
                                            </StandingsTableCell>
                                            {showExtendedStats && EXTENDED_STAT_HEADERS.map(({ key, againstKey }) => (
                                                <StandingsTableCell key={key}>
                                                    {showExtendedAgainst ? (team[againstKey] ?? 0) : (team[key] ?? 0)}
                                                </StandingsTableCell>
                                            ))}
                                        </StandingsTableRow>
                                    );
                                })}
                            </React.Fragment>
                            );
                        })}
                        {sortMode === 'playoffPicture' && !playoffPictureGroups && (
                            <StandingsMessageRow>
                                No playoff structure configured. Use Edit Season Structure to set division winners per division, wildcard count, and byes — then save. The Playoff picture will show how the bracket would look based on current standings.
                            </StandingsMessageRow>
                        )}
                        {sortMode === 'playoffPicture' && playoffPictureGroups && (
                            <>
                                <React.Fragment key="playoff-division-winners">
                                    <StandingsDivisionHeaderCell>
                                        Division winners
                                        {playoffPictureGroups.divisionWinners.length > 0 && ` (seeds 1–${playoffPictureGroups.divisionWinners.length})`}
                                    </StandingsDivisionHeaderCell>
                                    <StandingsDivisionHeaderSpacer />
                                    {playoffPictureGroups.divisionWinners.map((team, index) => {
                                        const casDiff = team.casualtiesFor - team.casualtiesAgainst;
                                        const seed = index + 1;
                                        const reason = playoffPictureRankReasons?.divisionWinnerReasons[index];
                                        return (
                                            <StandingsTableRow key={team.teamId} $isEven={index % 2 === 0}>
                                                <RankCell $isEven={index % 2 === 0}><RankCellBg $isEven={index % 2 === 0} /><span>{seed}</span></RankCell>
                                                <StandingsTableCell><CoachNameText>{team.divisionName}</CoachNameText></StandingsTableCell>
                                                <TeamNameCell>
                                                    <TeamNameText>{team.teamName}{renderClinchBadge(team.teamId)}</TeamNameText>
                                                    {reason && reason.decidedBy != null && (
                                                        <RankInfoWrapper
                                                            ref={rankInfoOpen?.teamId === team.teamId ? rankInfoWrapperRef : undefined}
                                                        >
                                                            <RankInfoButton
                                                                type="button"
                                                                title={reason.tooltip}
                                                                aria-label={`Why ${team.teamName} is seed ${seed}`}
                                                                aria-expanded={rankInfoOpen?.teamId === team.teamId}
                                                                onClick={() =>
                                                                    setRankInfoOpen((prev) =>
                                                                        prev?.teamId === team.teamId
                                                                            ? null
                                                                            : { divisionName: team.divisionName, teamId: team.teamId }
                                                                    )
                                                                }
                                                            >
                                                                <InfoIcon size="0.85em" />
                                                            </RankInfoButton>
                                                            {rankInfoOpen?.teamId === team.teamId && (
                                                                <RankInfoPopover>{reason.tooltip}</RankInfoPopover>
                                                            )}
                                                        </RankInfoWrapper>
                                                    )}
                                                </TeamNameCell>
                                                <CoachCell $isSeparator $isEven={index % 2 === 0}><CoachNameText>{team.coachName}</CoachNameText></CoachCell>
                                                <StandingsTableCell $afterSeparator>{team.gamesPlayed}</StandingsTableCell>
                                                <StandingsTableCell>{team.points}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>{team.divisionPoints}</StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.wins}</StandingsTableCell>
                                                <StandingsTableCell>{team.ties}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>{team.losses}</StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.touchdownsFor}</StandingsTableCell>
                                                <StandingsTableCell>{team.touchdownsAgainst}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>
                                                    <StandingsChangeIndicator $isPositive={team.diff > 0} $isNegative={team.diff < 0}>{team.diff > 0 ? '+' : ''}{team.diff}</StandingsChangeIndicator>
                                                </StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.casualtiesFor}</StandingsTableCell>
                                                <StandingsTableCell>{team.casualtiesAgainst}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>
                                                    <StandingsChangeIndicator $isPositive={casDiff > 0} $isNegative={casDiff < 0}>{casDiff > 0 ? '+' : ''}{casDiff}</StandingsChangeIndicator>
                                                </StandingsTableCell>
                                                {showExtendedStats && EXTENDED_STAT_HEADERS.map(({ key, againstKey }) => (
                                                    <StandingsTableCell key={key}>{showExtendedAgainst ? (team[againstKey] ?? 0) : (team[key] ?? 0)}</StandingsTableCell>
                                                ))}
                                            </StandingsTableRow>
                                        );
                                    })}
                                </React.Fragment>
                                <React.Fragment key="playoff-wildcards">
                                    <StandingsDivisionHeaderCell>
                                        Wildcards
                                        {playoffPictureGroups.wildcards.length > 0 && playoffPictureGroups.divisionWinners.length > 0 && ` (seeds ${playoffPictureGroups.divisionWinners.length + 1}–${playoffPictureGroups.divisionWinners.length + playoffPictureGroups.wildcards.length})`}
                                        {playoffPictureGroups.wildcards.length > 0 && playoffPictureGroups.divisionWinners.length === 0 && ` (seeds 1–${playoffPictureGroups.wildcards.length})`}
                                    </StandingsDivisionHeaderCell>
                                    <StandingsDivisionHeaderSpacer />
                                    {playoffPictureGroups.wildcards.map((team, index) => {
                                        const casDiff = team.casualtiesFor - team.casualtiesAgainst;
                                        const seed = playoffPictureGroups.divisionWinners.length + index + 1;
                                        const isLastWildcard = index === playoffPictureGroups.wildcards.length - 1;
                                        const reason = isLastWildcard && playoffPictureRankReasons?.lastWildcardReason
                                            ? playoffPictureRankReasons.lastWildcardReason
                                            : playoffPictureRankReasons?.wildcardReasons[index];
                                        return (
                                            <StandingsTableRow key={team.teamId} $isEven={index % 2 === 0}>
                                                <RankCell $isEven={index % 2 === 0}><RankCellBg $isEven={index % 2 === 0} /><span>{seed}</span></RankCell>
                                                <StandingsTableCell><CoachNameText>{team.divisionName}</CoachNameText></StandingsTableCell>
                                                <TeamNameCell>
                                                    <TeamNameText>{team.teamName}{renderClinchBadge(team.teamId)}</TeamNameText>
                                                    {reason && reason.decidedBy != null && (
                                                        <RankInfoWrapper
                                                            ref={rankInfoOpen?.teamId === team.teamId ? rankInfoWrapperRef : undefined}
                                                        >
                                                            <RankInfoButton
                                                                type="button"
                                                                title={reason.tooltip}
                                                                aria-label={`Why ${team.teamName} is seed ${seed}`}
                                                                aria-expanded={rankInfoOpen?.teamId === team.teamId}
                                                                onClick={() =>
                                                                    setRankInfoOpen((prev) =>
                                                                        prev?.teamId === team.teamId
                                                                            ? null
                                                                            : { divisionName: team.divisionName, teamId: team.teamId }
                                                                    )
                                                                }
                                                            >
                                                                <InfoIcon size="0.85em" />
                                                            </RankInfoButton>
                                                            {rankInfoOpen?.teamId === team.teamId && (
                                                                <RankInfoPopover>{reason.tooltip}</RankInfoPopover>
                                                            )}
                                                        </RankInfoWrapper>
                                                    )}
                                                </TeamNameCell>
                                                <CoachCell $isSeparator $isEven={index % 2 === 0}><CoachNameText>{team.coachName}</CoachNameText></CoachCell>
                                                <StandingsTableCell $afterSeparator>{team.gamesPlayed}</StandingsTableCell>
                                                <StandingsTableCell>{team.points}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>{team.divisionPoints}</StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.wins}</StandingsTableCell>
                                                <StandingsTableCell>{team.ties}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>{team.losses}</StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.touchdownsFor}</StandingsTableCell>
                                                <StandingsTableCell>{team.touchdownsAgainst}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>
                                                    <StandingsChangeIndicator $isPositive={team.diff > 0} $isNegative={team.diff < 0}>{team.diff > 0 ? '+' : ''}{team.diff}</StandingsChangeIndicator>
                                                </StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.casualtiesFor}</StandingsTableCell>
                                                <StandingsTableCell>{team.casualtiesAgainst}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>
                                                    <StandingsChangeIndicator $isPositive={casDiff > 0} $isNegative={casDiff < 0}>{casDiff > 0 ? '+' : ''}{casDiff}</StandingsChangeIndicator>
                                                </StandingsTableCell>
                                                {showExtendedStats && EXTENDED_STAT_HEADERS.map(({ key, againstKey }) => (
                                                    <StandingsTableCell key={key}>{showExtendedAgainst ? (team[againstKey] ?? 0) : (team[key] ?? 0)}</StandingsTableCell>
                                                ))}
                                            </StandingsTableRow>
                                        );
                                    })}
                                </React.Fragment>
                                <React.Fragment key="playoff-remaining">
                                    <StandingsDivisionHeaderCell>Remaining</StandingsDivisionHeaderCell>
                                    <StandingsDivisionHeaderSpacer />
                                    {playoffPictureGroups.remaining.map((team, index) => {
                                        const casDiff = team.casualtiesFor - team.casualtiesAgainst;
                                        const reason = playoffPictureRankReasons?.remainingReasons[index];
                                        return (
                                            <StandingsTableRow key={team.teamId} $isEven={index % 2 === 0}>
                                                <RankCell $isEven={index % 2 === 0}><RankCellBg $isEven={index % 2 === 0} /><span></span></RankCell>
                                                <StandingsTableCell><CoachNameText>{team.divisionName}</CoachNameText></StandingsTableCell>
                                                <TeamNameCell>
                                                    <TeamNameText>{team.teamName}{renderClinchBadge(team.teamId)}</TeamNameText>
                                                    {reason && reason.decidedBy != null && (
                                                        <RankInfoWrapper
                                                            ref={rankInfoOpen?.teamId === team.teamId ? rankInfoWrapperRef : undefined}
                                                        >
                                                            <RankInfoButton
                                                                type="button"
                                                                title={reason.tooltip}
                                                                aria-label={`Why ${team.teamName} is ranked ${index + 1} in remaining`}
                                                                aria-expanded={rankInfoOpen?.teamId === team.teamId}
                                                                onClick={() =>
                                                                    setRankInfoOpen((prev) =>
                                                                        prev?.teamId === team.teamId
                                                                            ? null
                                                                            : { divisionName: team.divisionName, teamId: team.teamId }
                                                                    )
                                                                }
                                                            >
                                                                <InfoIcon size="0.85em" />
                                                            </RankInfoButton>
                                                            {rankInfoOpen?.teamId === team.teamId && (
                                                                <RankInfoPopover>{reason.tooltip}</RankInfoPopover>
                                                            )}
                                                        </RankInfoWrapper>
                                                    )}
                                                </TeamNameCell>
                                                <CoachCell $isSeparator $isEven={index % 2 === 0}><CoachNameText>{team.coachName}</CoachNameText></CoachCell>
                                                <StandingsTableCell $afterSeparator>{team.gamesPlayed}</StandingsTableCell>
                                                <StandingsTableCell>{team.points}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>{team.divisionPoints}</StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.wins}</StandingsTableCell>
                                                <StandingsTableCell>{team.ties}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>{team.losses}</StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.touchdownsFor}</StandingsTableCell>
                                                <StandingsTableCell>{team.touchdownsAgainst}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>
                                                    <StandingsChangeIndicator $isPositive={team.diff > 0} $isNegative={team.diff < 0}>{team.diff > 0 ? '+' : ''}{team.diff}</StandingsChangeIndicator>
                                                </StandingsTableCell>
                                                <StandingsTableCell $afterSeparator>{team.casualtiesFor}</StandingsTableCell>
                                                <StandingsTableCell>{team.casualtiesAgainst}</StandingsTableCell>
                                                <StandingsTableCell $isSeparator>
                                                    <StandingsChangeIndicator $isPositive={casDiff > 0} $isNegative={casDiff < 0}>{casDiff > 0 ? '+' : ''}{casDiff}</StandingsChangeIndicator>
                                                </StandingsTableCell>
                                                {showExtendedStats && EXTENDED_STAT_HEADERS.map(({ key, againstKey }) => (
                                                    <StandingsTableCell key={key}>{showExtendedAgainst ? (team[againstKey] ?? 0) : (team[key] ?? 0)}</StandingsTableCell>
                                                ))}
                                            </StandingsTableRow>
                                        );
                                    })}
                                </React.Fragment>
                            </>
                        )}
                        {sortMode === 'leagueWide' && flatTeamsLeagueWide.map((team, index) => {
                            const casDiff = team.casualtiesFor - team.casualtiesAgainst;
                            const rank = index + 1;
                            const reason = leagueWideRankReasons[index];
                            return (
                                <StandingsTableRow key={team.teamId} $isEven={index % 2 === 0}>
                                    <RankCell $isEven={index % 2 === 0}><RankCellBg $isEven={index % 2 === 0} /><span>{rank}</span></RankCell>
                                    <StandingsTableCell>
                                        <CoachNameText>{team.divisionName}</CoachNameText>
                                    </StandingsTableCell>
                                    <TeamNameCell>
                                        <TeamNameText>{team.teamName}{renderClinchBadge(team.teamId)}</TeamNameText>
                                        {reason && reason.decidedBy != null && (
                                            <RankInfoWrapper
                                                ref={
                                                    rankInfoOpen?.teamId === team.teamId ? rankInfoWrapperRef : undefined
                                                }
                                            >
                                                <RankInfoButton
                                                    type="button"
                                                    title={reason.tooltip}
                                                    aria-label={`Why ${team.teamName} is rank ${rank}`}
                                                    aria-expanded={rankInfoOpen?.teamId === team.teamId}
                                                    onClick={() =>
                                                        setRankInfoOpen((prev) =>
                                                            prev?.teamId === team.teamId
                                                                ? null
                                                                : { divisionName: team.divisionName, teamId: team.teamId }
                                                        )
                                                    }
                                                >
                                                    <InfoIcon size="0.85em" />
                                                </RankInfoButton>
                                                {rankInfoOpen?.teamId === team.teamId && (
                                                    <RankInfoPopover>{reason.tooltip}</RankInfoPopover>
                                                )}
                                            </RankInfoWrapper>
                                        )}
                                    </TeamNameCell>
                                    <CoachCell $isSeparator $isEven={index % 2 === 0}>
                                        <CoachNameText>{team.coachName}</CoachNameText>
                                    </CoachCell>
                                    <StandingsTableCell $afterSeparator>{team.gamesPlayed}</StandingsTableCell>
                                    <StandingsTableCell>{team.points}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>{team.divisionPoints}</StandingsTableCell>
                                    <StandingsTableCell $afterSeparator>{team.wins}</StandingsTableCell>
                                    <StandingsTableCell>{team.ties}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>{team.losses}</StandingsTableCell>
                                    <StandingsTableCell $afterSeparator>{team.touchdownsFor}</StandingsTableCell>
                                    <StandingsTableCell>{team.touchdownsAgainst}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>
                                        <StandingsChangeIndicator $isPositive={team.diff > 0} $isNegative={team.diff < 0}>
                                            {team.diff > 0 ? '+' : ''}{team.diff}
                                        </StandingsChangeIndicator>
                                    </StandingsTableCell>
                                    <StandingsTableCell $afterSeparator>{team.casualtiesFor}</StandingsTableCell>
                                    <StandingsTableCell>{team.casualtiesAgainst}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>
                                        <StandingsChangeIndicator $isPositive={casDiff > 0} $isNegative={casDiff < 0}>
                                            {casDiff > 0 ? '+' : ''}{casDiff}
                                        </StandingsChangeIndicator>
                                    </StandingsTableCell>
                                    {showExtendedStats && EXTENDED_STAT_HEADERS.map(({ key, againstKey }) => (
                                        <StandingsTableCell key={key}>
                                            {showExtendedAgainst ? (team[againstKey] ?? 0) : (team[key] ?? 0)}
                                        </StandingsTableCell>
                                    ))}
                                </StandingsTableRow>
                            );
                        })}
                        {sortMode === 'byColumn' && flatTeamsByColumn.map((team, index) => {
                            const casDiff = team.casualtiesFor - team.casualtiesAgainst;
                            const rank = index + 1;
                            return (
                                <StandingsTableRow key={team.teamId} $isEven={index % 2 === 0}>
                                    <RankCell $isEven={index % 2 === 0}><RankCellBg $isEven={index % 2 === 0} /><span>{rank}</span></RankCell>
                                    <StandingsTableCell>
                                        <CoachNameText>{team.divisionName}</CoachNameText>
                                    </StandingsTableCell>
                                    <TeamNameCell>
                                        <TeamNameText>{team.teamName}{renderClinchBadge(team.teamId)}</TeamNameText>
                                    </TeamNameCell>
                                    <CoachCell $isSeparator $isEven={index % 2 === 0}>
                                        <CoachNameText>{team.coachName}</CoachNameText>
                                    </CoachCell>
                                    <StandingsTableCell $afterSeparator>{team.gamesPlayed}</StandingsTableCell>
                                    <StandingsTableCell>{team.points}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>{team.divisionPoints}</StandingsTableCell>
                                    <StandingsTableCell $afterSeparator>{team.wins}</StandingsTableCell>
                                    <StandingsTableCell>{team.ties}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>{team.losses}</StandingsTableCell>
                                    <StandingsTableCell $afterSeparator>{team.touchdownsFor}</StandingsTableCell>
                                    <StandingsTableCell>{team.touchdownsAgainst}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>
                                        <StandingsChangeIndicator $isPositive={team.diff > 0} $isNegative={team.diff < 0}>
                                            {team.diff > 0 ? '+' : ''}{team.diff}
                                        </StandingsChangeIndicator>
                                    </StandingsTableCell>
                                    <StandingsTableCell $afterSeparator>{team.casualtiesFor}</StandingsTableCell>
                                    <StandingsTableCell>{team.casualtiesAgainst}</StandingsTableCell>
                                    <StandingsTableCell $isSeparator>
                                        <StandingsChangeIndicator $isPositive={casDiff > 0} $isNegative={casDiff < 0}>
                                            {casDiff > 0 ? '+' : ''}{casDiff}
                                        </StandingsChangeIndicator>
                                    </StandingsTableCell>
                                    {showExtendedStats && EXTENDED_STAT_HEADERS.map(({ key, againstKey }) => (
                                        <StandingsTableCell key={key}>
                                            {showExtendedAgainst ? (team[againstKey] ?? 0) : (team[key] ?? 0)}
                                        </StandingsTableCell>
                                    ))}
                                </StandingsTableRow>
                            );
                        })}
                    </StandingsTableGrid>
                    </TableContainer>
                    {(sortMode === 'byDivision' || sortMode === 'leagueWide') && bumpChartData && (
                        <div style={{ marginTop: '0.5em' }}>
                            {sortMode === 'byDivision' && divisionNames.length > 1 && (
                                <div style={{ marginBottom: '0.5em', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                    <label htmlFor="bump-division-select" style={{ fontSize: '0.9em', fontWeight: 600 }}>Division:</label>
                                    <select
                                        id="bump-division-select"
                                        value={selectedBumpDivision}
                                        onChange={(e) => setSelectedBumpDivision(e.target.value)}
                                        style={{ padding: '0.25em 0.5em', fontSize: '0.9em', minWidth: '180px' }}
                                    >
                                        {divisionNames.map(name => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <StandingsBumpChart
                                title={bumpChartData.title}
                                weekNames={bumpChartData.weekNames}
                                series={bumpChartData.series}
                            />
                        </div>
                    )}
                </>
            ) : (
                <p>Add divisions and weeks to see the standings.</p>
            )}
        </StandingsSection>
    );
}; 